import React from 'react';
import styled, { useTheme } from 'styled-components';
import { useSelector } from 'react-redux';
import { FormattedMessage, useIntl } from 'react-intl';

import { lighten } from 'utils/colours';
import messages, { graphLabels, tooltipLabels } from './messages';

import overnightStatsModule from 'containers/App/data/endpoints/overnightStats';

import Tooltip from 'components/charts/Tooltip';
import TooltipContent from './components/TooltipContent';
import OvernightBarChart from './components/OvernightBarChart';

const Container = styled.div`
    width: 100%;
    padding: 15px;
    text-align: center;
`;

/**
 * Check is a row is fully empty, i.e. no guaranteed/nonGuaranteed data
 */
const isEmpty = (row) =>
    row.arrivals.rooms.guaranteed === 0 &&
    row.arrivals.rooms.nonGuaranteed === 0 &&
    row.departures.rooms.guaranteed === 0 &&
    row.departures.rooms.nonGuaranteed === 0 &&
    row.inHouse.rooms.guaranteed === 0 &&
    row.inHouse.rooms.nonGuaranteed === 0;

export const OvernightStats = ({ rawData = null }) => {
    const { formatNumber, formatMessage } = useIntl();
    const theme = useTheme();

    const { start } = useSelector(overnightStatsModule.selectors.selectFilters);
    const data = rawData.find((row) => row.stayDate === start);

    if (!data || isEmpty(data))
        return (
            <Container>
                <FormattedMessage {...messages.noData} />
            </Container>
        );

    const graphData = [
        // Arrivals
        {
            title: formatMessage(graphLabels.arrivals),
            ...data.arrivals,
            accessors: {
                guaranteed: (row) => row.rooms.guaranteed,
                nonGuaranteed: (row) => row.rooms.nonGuaranteed,
            },
            colours: {
                guaranteed: theme.chartsPalette.cyan,
                nonGuaranteed: lighten(theme.chartsPalette.cyan, 15),
            },
        },
        // Departures
        {
            title: formatMessage(graphLabels.departures),
            ...data.departures,
            accessors: {
                guaranteed: (row) => -row.rooms.guaranteed,
                nonGuaranteed: (row) => -row.rooms.nonGuaranteed,
            },
            colours: {
                guaranteed: theme.chartsPalette.purple,
                nonGuaranteed: lighten(theme.chartsPalette.purple, 15),
            },
        },
        // In-House
        {
            title: formatMessage(graphLabels.inHouse),
            ...data.inHouse,
            accessors: {
                guaranteed: (row) => row.rooms.guaranteed,
                nonGuaranteed: (row) => row.rooms.nonGuaranteed,
            },
            colours: {
                guaranteed: theme.chartsPalette.blue,
                nonGuaranteed: lighten(theme.chartsPalette.blue, 15),
            },
        },
    ];

    const tooltipSections = [
        {
            key: 'rooms',
            titleMessage: tooltipLabels.rooms,
            getGuaranteedValue: (row) => formatNumber(row.rooms.guaranteed),
            getNonGuaranteedValue: (row) => formatNumber(row.rooms.nonGuaranteed),
        },
        {
            key: 'adults',
            titleMessage: tooltipLabels.adults,
            getGuaranteedValue: (row) => formatNumber(row.adults.guaranteed),
            getNonGuaranteedValue: (row) => formatNumber(row.adults.nonGuaranteed),
        },
        {
            key: 'children',
            titleMessage: tooltipLabels.children,
            getGuaranteedValue: (row) => formatNumber(row.children.guaranteed),
            getNonGuaranteedValue: (row) => formatNumber(row.children.nonGuaranteed),
        },
    ];

    const tooltipMessages = {
        guaranteed: tooltipLabels.guaranteed,
        nonGuaranteed: tooltipLabels.nonGuaranteed,
    };

    const tooltipFormatter = (_value, name, { payload }) => (
        <TooltipContent type={name} sections={tooltipSections} messages={tooltipMessages} row={payload} />
    );

    const graphSettings = {
        data: graphData,
        bars: ['guaranteed', 'nonGuaranteed'],
        xAxisKey: 'title',
        yAxisLabel: formatMessage(messages.yAxisLabel),
        yAxisTickFormatter: formatNumber,
        tooltip: <Tooltip formatter={tooltipFormatter} />,
    };
    return (
        <Container>
            <OvernightBarChart {...graphSettings} />
        </Container>
    );
};

export default OvernightStats;
